<script>
import { Query, useQueryClient } from "@sveltestack/svelte-query"
import { startOfWeek, endOfWeek, format } from "date-fns"
import API from "@local/utils/api"
import List from "@local/components/List.svelte"
import Pager from "@local/components/Pager.svelte"
import EmptyMessage from "@local/components/EmptyMessage.svelte"
import TextInput from "@local/components/form/TextInput.svelte"
import Select from "@local/components/form/Select.svelte"
import Icon from "@local/components/images/Icon.svelte"

const rankingOptions = [
  { label: "Platinum", value: 1 },
  { label: "Gold", value: 2 },
  { label: "Silver", value: 3 },
  { label: "Bronze", value: 4 },
  { label: "Limited Purchase", value: 5 },
  { label: "No Purchase", value: 10 },
]

const queryClient = useQueryClient()

let queryVal = 1
let fromDateVal = format(startOfWeek(new Date()), "yyyy-MM-dd")
let toDateVal = format(endOfWeek(new Date()), "yyyy-MM-dd")
let page = 0
let perPage = 20
let filters = {
  query: 1,
  expiresAtFrom: fromDateVal,
  expiresAtTo: toDateVal,
}

let isUploading = false

function search(pageNumber = 0) {
  page = pageNumber
  const newFilters = {
    query: queryVal,
  }
  if (fromDateVal) newFilters.expiresAtFrom = fromDateVal
  if (toDateVal) newFilters.expiresAtTo = toDateVal
  filters = newFilters
}

async function fetchWorkflows(filters, page) {
  const defKey = "admPurchases"
  const searchParams = {
    role: "admin",
    page,
    perPage,
    ...filters,
  }
  const response = await API.get(`lists/def-key/${defKey}`, {
    searchParams,
    timeout: 25000,
  })
  const rows = await response.json()
  const total = response.headers.get("x-total-count")
  const hasNextPage = (page + 1) * perPage < total

  return { rows, hasNextPage }
}

async function fetchWorkflowsForCSV(filters) {
  const defKey = "admPurchases"
  const searchParams = {
    role: "admin",
    page: 0,
    perPage: 10000000,
    ...filters,
  }
  const response = await API.get(`lists/def-key/${defKey}`, {
    searchParams,
    timeout: 25000,
  })
  const rows = await response.json()

  let csv
  if (rows.length > 0) {
    csv = rows[0].columns.map((row) => row.label).join(",") + "\n"

    csv += rows
      .map((row) => row.columns.map((row) => row.value).join(","))
      .join("\n")
  } else {
    csv = ""
  }

  // Create and trigger download
  const blob = new Blob([csv], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = "report.csv"
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function handleUpload(evt = {}) {
  const inputFiles = evt.target.files

  const reader = new FileReader()
  reader.onload = (readerEvent) => {
    const file = {
      src: readerEvent.target.result,
      name: inputFiles[0].name,
    }

    isUploading = true
    API.patch(`admin/reports/purchases`, {
      json: { csvBase64: file.src },
      timeout: 300000,
    })
      .then(() => {
        queryClient.invalidateQueries()
        alert("Processing records")
      })
      .catch((err) => {
        if (err.name === "TimeoutError") {
          alert(
            "Upload took too long and was cancelled (timeout). Try again later or send a smaller file."
          )
        } else {
          console.warn("UPLOAD ERROR", err)
          // alert(`Failed updating records`)
        }
      })
      .finally(() => {
        isUploading = false
      })
  }

  reader.readAsDataURL(inputFiles[0])
}

function resetInput(evt = {}) {
  evt.target.value = ""
}

$: queryOptions = {
  queryKey: ["reports/purchases", JSON.stringify(filters), page],
  queryFn: () => fetchWorkflows(filters, page),
  keepPreviousData: true,
  refetchOnWindowFocus: false,
}
</script>

<Query options="{queryOptions}">
  <div class="p-5" slot="query" let:queryResult>
    {#if queryResult.isLoading}
      <div></div>
    {:else if queryResult.error}
      <EmptyMessage icon="error">{queryResult.error}</EmptyMessage>
    {:else}
      <div class="flex flex-row items-center justify-between mb-6">
        <h2 class="h2">Report: Purchases</h2>
      </div>
      <div class="flex items-end mb-2">
        <Pager
          bind:page="{page}"
          hasNextPage="{queryResult.data.hasNextPage}"
          isPreviousData="{queryResult.isPreviousData}"
        />
        <form
          class="flex items-end w-full gap-2 ml-2"
          on:submit|preventDefault="{() => search(0)}"
        >
          <div
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 cursor-pointer btn btn-outline relative"
          >
            <input
              type="file"
              class="opacity-0 w-full h-full absolute top-0 left-0"
              on:change="{handleUpload}"
              on:click="{resetInput}"
              disabled="{isUploading}"
            />
            <Icon size="3xl">upload_file</Icon>
          </div>
          {#if isUploading}
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            >
              <div
                class="bg-white rounded-lg shadow-lg p-8 flex flex-col items-center"
              >
                <Icon size="4xl" class="animate-spin text-blue-600 mb-4"
                  >upload_file</Icon
                >
                <span class="text-lg font-semibold text-gray-800"
                  >Uploading file, please wait...</span
                >
              </div>
            </div>
          {/if}
          <TextInput bind:value="{fromDateVal}" label="From" type="date" />
          <TextInput bind:value="{toDateVal}" label="To" type="date" />
          <Select
            bind:value="{queryVal}"
            label="Rankings"
            options="{rankingOptions}"
          />
          <button
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 btn btn-outline"
            type="submit"><Icon size="3xl">search</Icon></button
          >

          <button
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 btn btn-outline"
            type="button"
            on:click="{() => fetchWorkflowsForCSV(filters)}"
            ><Icon size="3xl">download</Icon></button
          >
        </form>
      </div>
      {#if queryResult.data.rows.length > 0}
        <List rows="{queryResult.data.rows}" />
      {:else}
        <EmptyMessage icon="check">No records</EmptyMessage>
      {/if}
      <div class="flex items-center justify-center mt-2">
        <Pager
          class="md:hidden"
          bind:page="{page}"
          hasNextPage="{queryResult.data.hasNextPage}"
          isPreviousData="{queryResult.isPreviousData}"
        />
      </div>
    {/if}
  </div>
</Query>
